import { Controller } from "@hotwired/stimulus"
import SlimSelect from "slim-select"
import { slimSelectSettings } from "../helpers/slim_select_helpers"

const MIN_SEARCH_LENGTH = 2
const SEARCH_DEBOUNCE_MS = 200
// Value of the "„X" anlegen / übernehmen" row the search appends; never a real article id.
const CREATE_PREFIX = "__create__:"
// Important: SlimSelect styles .ss-option through three classes, which outranks a utility.
const CREATE_OPTION_CLASS = "font-medium text-green-700!"

// Article picker of a report material row: searches the Artikelstamm server-side
// (Costing::ArticlesController#search), fills the row's name and unit from the picked
// article, and turns an unknown term into a new Artikel via #quick_create when the account
// may create articles — otherwise, on forms that set `allowFreeText` because they keep a
// free-text name, into that name (a `text:` option the server drops from the article id, so
// the row keeps only the name). Without either, only existing articles can be picked. The
// select is rendered with only the current pick as option; everything else comes from the
// search, because the master can hold thousands of rows. Opening the picker lists the most
// recently changed articles, so it never looks empty before anything is typed.
//
// Creating is a labelled row in the results, not SlimSelect's `addable`: allowDeselect keeps a
// blank option in every result list, so SlimSelect never shows its addable text and only a
// bare "+" button was left. Every text comes from the view (I18n); `{value}` in the addable
// texts stands for the typed term.
//
// Forms that pass `planned` (the open planned lines of the report's site, BAUTASK-307) show them
// as their own group above the hits, filtered by the typed term without a request. Their values
// carry the `requirement:` prefix the server turns into the row's planned line; picking one shows
// the `plannedInfo` hint and locks the unit to the line's.
const PLANNED_PREFIX = "requirement:"
const LOCKED_UNIT_CLASSES = ["pointer-events-none", "bg-gray-50", "text-gray-500"]

export default class extends Controller {
  static targets = ["select", "name", "unit", "quantity", "plannedInfo", "plannedQuantity"]
  static values = {
    searchUrl: String,
    createUrl: String,
    canCreate: { type: Boolean, default: false },
    allowFreeText: { type: Boolean, default: false },
    searchPlaceholder: String,
    searchOrCreatePlaceholder: String,
    addableText: String,
    freeTextPlaceholder: String,
    freeTextAddableText: String,
    searchError: String,
    emptyText: String,
    planned: { type: Array, default: [] },
    plannedLabel: String,
  }

  connect() {
    if (!this.hasSelectTarget) return

    const config = {
      select: this.selectTarget,
      settings: slimSelectSettings({
        allowDeselect: true,
        showSearch: true,
        searchPlaceholder: this.searchPlaceholderText(),
        placeholderText: this.selectTarget.dataset.placeholder,
      }),
      events: {
        search: (search, _selected, catalog) => this.search(search, catalog),
        afterChange: (newVal) => this.handleSelectionChange(newVal),
        afterOpen: () => this.loadRecent(),
      },
    }

    this.slimSelect = new SlimSelect(config)
  }

  // The row that turns the typed term into a new Artikel (or a free-text name), or null when
  // the form allows neither or a hit already carries exactly that name.
  createOption(term, options) {
    let text = null
    if (this.canCreateValue) text = this.addableTextValue
    else if (this.allowFreeTextValue) text = this.freeTextAddableTextValue
    if (!text) return null

    const lowered = term.toLowerCase()
    if (options.some((option) => (option.data.name || option.text).toLowerCase() === lowered)) {
      return null
    }

    return {
      text: text.replace("{value}", term),
      value: `${CREATE_PREFIX}${term}`,
      class: CREATE_OPTION_CLASS,
    }
  }

  searchPlaceholderText() {
    if (this.canCreateValue && this.searchOrCreatePlaceholderValue) {
      return this.searchOrCreatePlaceholderValue
    }
    if (!this.canCreateValue && this.allowFreeTextValue && this.freeTextPlaceholderValue) {
      return this.freeTextPlaceholderValue
    }
    return this.searchPlaceholderValue
  }

  disconnect() {
    this.cancelSearch()
    if (this.slimSelect) this.slimSelect.destroy()
  }

  // One request per settled term; a superseded search stays pending so SlimSelect never
  // renders a stale list over newer hits (same reasoning as select_controller#serverSearch).
  // A cleared search (slim-select ≥ 4.4 passes it on) gets the recent list back: from `catalog`
  // once loaded, else fetched here — a term typed before it arrived aborted that load.
  search(search, catalog) {
    const term = search.trim()
    this.cancelSearch()
    if (term.length === 0) return this.recentLoaded ? catalog : this.recentOptions()
    const planned = this.plannedGroup(this.plannedOptions(term))
    if (term.length < MIN_SEARCH_LENGTH) return Promise.resolve(planned)

    return this.fetchOptions(`q=${encodeURIComponent(term)}`).then((options) => {
      const create = this.createOption(term, [...this.plannedOptions(term), ...options])
      return [...planned, ...options, ...(create ? [create] : [])]
    })
  }

  // The planned lines whose label contains the term (all of them for a blank term), as options.
  plannedOptions(term = "") {
    const lowered = term.toLowerCase()
    return this.plannedValue
      .filter((line) => line.text.toLowerCase().includes(lowered))
      .map((line) => ({ text: line.text, value: line.value, data: line.data || {} }))
  }

  // SlimSelect optgroup holding the planned lines; nothing when none match.
  plannedGroup(options) {
    return options.length > 0 ? [{ label: this.plannedLabelValue, options }] : []
  }

  // Fills the opened, still empty picker with the most recently changed articles. Shares the
  // abort handle with #search, so a term typed before the list arrives wins.
  loadRecent() {
    this.cancelSearch()
    this.recentLoaded = false
    this.recentOptions()
      .then((data) => {
        this.slimSelect.setData(data)
        this.recentLoaded = true
      })
      .catch(() => {})
  }

  // The most recently changed articles as SlimSelect data, the current pick kept on top.
  recentOptions() {
    return this.fetchOptions("recent=1").then((options) => {
      const selected = this.slimSelect
        .getData()
        .filter((option) => option.selected && option.value !== "")
      const pickedValues = new Set(selected.map((option) => option.value))
      const rows = options.filter((option) => !pickedValues.has(option.value))
      const planned = this.plannedGroup(
        this.plannedOptions().filter((option) => !pickedValues.has(option.value)),
      )
      if (
        rows.length === 0 &&
        planned.length === 0 &&
        selected.length === 0 &&
        this.emptyTextValue
      ) {
        rows.push({ text: this.emptyTextValue, value: "__empty__", disabled: true })
      }
      // The blank placeholder keeps "nothing picked" a choice: without it a single select
      // falls back to its first option and the recent list would pick an article on open.
      const placeholder = {
        text: "",
        value: "",
        placeholder: true,
        selected: selected.length === 0,
      }
      return [placeholder, ...selected, ...planned, ...rows]
    })
  }

  // Without a search URL (the account may not search the Artikelstamm) the picker offers only the
  // planned lines and free text.
  fetchOptions(query) {
    if (!this.searchUrlValue) return Promise.resolve([])

    return new Promise((resolve, reject) => {
      const abort = new AbortController()
      this.searchAbort = abort
      this.searchTimer = setTimeout(() => {
        fetch(`${this.searchUrlValue}?${query}`, {
          headers: { Accept: "application/json" },
          signal: abort.signal,
        })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            return response.json()
          })
          .then((rows) => resolve(rows.map((row) => this.rowToOption(row))))
          .catch((error) => {
            if (error.name === "AbortError") return
            reject(new Error(this.searchErrorValue))
          })
      }, SEARCH_DEBOUNCE_MS)
    })
  }

  cancelSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    if (this.searchAbort) this.searchAbort.abort()
    this.searchTimer = null
    this.searchAbort = null
  }

  // `data` lands on the <option> as data-* attributes (SlimSelect kebab-cases the keys):
  // name and unit are what the row copies from the pick.
  rowToOption(row) {
    return { text: row.label, value: String(row.id), data: row.data || {} }
  }

  handleSelectionChange(newVal) {
    const picked = newVal && newVal.length > 0 ? newVal[0] : null
    if (picked?.value?.startsWith(CREATE_PREFIX)) {
      this.resolveCreateOption(picked.value.slice(CREATE_PREFIX.length))
      return
    }
    // Matched by value, not by an attribute selector: a free-text value is whatever the user
    // typed, quotes and backslashes included.
    const option = picked
      ? Array.from(this.selectTarget.options).find((opt) => opt.value === picked.value)
      : null

    if (this.hasNameTarget) {
      this.nameTarget.value = option ? option.dataset.name || picked.text : ""
    }

    const unit = option?.dataset.unit
    if (unit && this.hasUnitTarget) {
      this.setSelectValue(this.unitTarget, unit)
      this.unitTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    this.showPlanned(
      picked?.value?.startsWith(PLANNED_PREFIX) ? option?.dataset.planned || "" : null,
    )

    // The article's Vorgabemenge only fills an empty quantity — a typed amount wins.
    const quantity = option?.dataset.defaultQuantity
    if (quantity && this.hasQuantityTarget && this.quantityTarget.value.trim() === "") {
      this.quantityTarget.value = quantity
      this.quantityTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  // Swaps the picked "anlegen" row for the real option; selecting that fires afterChange again,
  // which fills name and unit. A failed create clears the pick rather than keep a fake value.
  async resolveCreateOption(term) {
    const option = this.canCreateValue ? await this.createArticle(term) : this.freeTextOption(term)
    if (!option) {
      this.slimSelect.setSelected("")
      return
    }

    this.slimSelect.addOption(option)
    this.slimSelect.setSelected(option.value)
  }

  freeTextOption(name) {
    const trimmedName = name.trim()
    if (trimmedName.length === 0) return false

    return { text: trimmedName, value: `text:${trimmedName}`, data: { name: trimmedName } }
  }

  async createArticle(name) {
    const trimmedName = name.trim()
    if (trimmedName.length === 0) return false

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ name: trimmedName }),
      })

      if (!response.ok) return false

      const data = await response.json()
      return {
        text: data.name,
        value: String(data.id),
        data: {
          name: data.name,
          unit: data.unit || "",
          defaultQuantity: data.default_quantity || "",
        },
      }
    } catch (error) {
      console.error("Article creation failed:", error)
      return false
    }
  }

  // `text` is the planned line's quantities for a planned pick, null for anything else. The unit
  // stays submitted (a disabled field would not be), it only cannot be changed.
  showPlanned(text) {
    const planned = text !== null
    if (this.hasPlannedInfoTarget) this.plannedInfoTarget.classList.toggle("hidden", !planned)
    if (this.hasPlannedQuantityTarget) this.plannedQuantityTarget.textContent = text || ""
    if (!this.hasUnitTarget) return

    LOCKED_UNIT_CLASSES.forEach((name) => this.unitTarget.classList.toggle(name, planned))
    if (planned) {
      this.unitTarget.setAttribute("tabindex", "-1")
      this.unitTarget.setAttribute("aria-disabled", "true")
    } else {
      this.unitTarget.removeAttribute("tabindex")
      this.unitTarget.removeAttribute("aria-disabled")
    }
  }

  setSelectValue(selectElement, value) {
    for (let i = 0; i < selectElement.options.length; i++) {
      if (selectElement.options[i].value == value) {
        selectElement.selectedIndex = i
        return
      }
    }
    // A unit the catalogue list does not offer (a copied legacy string) still has to be shown.
    const option = document.createElement("option")
    option.value = value
    option.textContent = value
    option.selected = true
    selectElement.appendChild(option)
  }
}
